import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts } from '../../services/api';
import { useCart } from '../../context/CartContext';
import {
  ShoppingCartIcon,
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  HeartIcon,
  MapPinIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
  CubeIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  Bars3Icon,
  XMarkIcon,
  StarIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// ─── MOCK DATA (used when backend is not connected) ──────────────────────────
const MOCK_PRODUCTS = [
  { id: 1,  name: 'Fresh Cow Milk',      price: 25,  category: 'Milk',   emoji: '🥛', description: 'Pure cow milk delivered fresh every morning. No preservatives, no adulteration.', badge: 'Bestseller', stock: 24, image: null },
  { id: 2,  name: 'Farm Fresh Eggs',     price: 45,  category: 'Eggs',   emoji: '🥚', description: 'Free-range farm fresh eggs. Rich in protein and omega-3 fatty acids.', badge: 'Protein Rich', stock: 18, image: null },
  { id: 3,  name: 'White Butter',        price: 50,  category: 'Butter', emoji: '🧈', description: 'Creamy white butter made from fresh cream. Perfect for your daily bread.', badge: 'Pure', stock: 12, image: null },
  { id: 4,  name: 'Cheese Block',        price: 80,  category: 'Cheese', emoji: '🧀', description: 'Premium processed cheese blocks. Perfect for sandwiches and cooking.', badge: 'Premium', stock: 8,  image: null },
  { id: 5,  name: 'Fresh Curd',          price: 30,  category: 'Curd',   emoji: '🥛', description: 'Thick set curd made from pure cow milk. Great source of probiotics.', badge: 'Probiotic', stock: 15, image: null },
  { id: 6,  name: 'Buttermilk',          price: 20,  category: 'Milk',   emoji: '🧃', description: 'Refreshing chilled buttermilk. Perfect for summer hydration.', badge: 'Refreshing', stock: 20, image: null },
  { id: 7,  name: 'Vanilla Ice Cream',   price: 60,  category: 'Frozen', emoji: '🍦', description: 'Creamy vanilla ice cream made with fresh milk. A family favourite!', badge: 'Treat', stock: 10, image: null },
  { id: 8,  name: 'Chocolate Milk',      price: 35,  category: 'Milk',   emoji: '🥛', description: 'Delicious chocolate flavoured milk. Kids love it!', badge: "Kids' Choice", stock: 16, image: null },
  { id: 9,  name: 'Paneer Block',        price: 90,  category: 'Cheese', emoji: '🧀', description: 'Soft, fresh paneer made from pure cow milk. Ideal for curries.', badge: 'Fresh', stock: 14, image: null },
  { id: 10, name: 'Ghee (500ml)',        price: 150, category: 'Butter', emoji: '🧈', description: 'Pure desi ghee, slow-churned using traditional methods.', badge: 'Pure Desi', stock: 9,  image: null },
  { id: 11, name: 'Skimmed Milk',        price: 22,  category: 'Milk',   emoji: '🥛', description: 'Low-fat skimmed milk. Perfect for a healthy lifestyle.', badge: 'Low Fat', stock: 20, image: null },
  { id: 12, name: 'Strawberry Yogurt',   price: 40,  category: 'Curd',   emoji: '🍓', description: 'Creamy strawberry flavored yogurt. A healthy snack anytime.', badge: 'Fruity', stock: 12, image: null },
];
// ─────────────────────────────────────────────────────────────────────────────

const Products = () => {
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();

  const [products, setProducts]               = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [usingMockData, setUsingMockData]     = useState(false);
  const [searchTerm, setSearchTerm]           = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [scrolled, setScrolled]               = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]   = useState(false);
  const [wishlist, setWishlist]               = useState([]);

  const currentYear = new Date().getFullYear();

  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { filterProducts(); }, [searchTerm, selectedCategory, products]);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadProducts = async () => {
    try {
      const response = await getProducts();
      const data = response?.data?.products;
      if (data && data.length > 0) {
        setProducts(data);
        setFilteredProducts(data);
        setUsingMockData(false);
      } else {
        throw new Error('No products from backend');
      }
    } catch {
      // Backend not connected – show mock data silently
      setProducts(MOCK_PRODUCTS);
      setFilteredProducts(MOCK_PRODUCTS);
      setUsingMockData(true);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    setFilteredProducts(filtered);
  };

  const categories = ['all', ...new Set(products.map(p => p.category).filter(Boolean))];

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart! 🛒`, { duration: 2000 });
  };

  const toggleWishlist = (id) => {
    setWishlist(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const cartCount = cart.reduce((total, item) => total + (item.quantity || 1), 0);

  // Helper: get product image src (real image URL or null → fall through to emoji)
  const getImageSrc = (product) => {
    if (product.image) return product.image;
    if (product.imageUrl) return product.imageUrl;
    return null;
  };

  // ─── STYLES ──────────────────────────────────────────────────────────────
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');

    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      color: #1a2e1a;
      overflow-x: hidden;
      background: #f9fafb;
    }

    /* ── CONTAINER ── */
    .fp-container {
      width: 100%;
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 1rem;
    }
    @media(min-width:640px){ .fp-container{ padding: 0 1.5rem; } }
    @media(min-width:1024px){ .fp-container{ padding: 0 2rem; } }

    /* ── HEADER ── */
    .fp-header {
      position: sticky;
      top: 0;
      z-index: 50;
      width: 100%;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(229,231,235,0.6);
      transition: all 0.35s ease;
    }
    .fp-header.scrolled {
      box-shadow: 0 4px 20px rgba(0,0,0,0.07);
      border-bottom-color: rgba(5,150,105,0.15);
    }

    .fp-header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 60px;
      gap: 0.5rem;
    }
    @media(min-width:768px){ .fp-header-inner{ height: 72px; } }

    /* Logo */
    .fp-logo {
      display: flex; align-items: center; gap: 0.5rem;
      flex-shrink: 0; cursor: pointer;
    }
    .fp-logo img { height: 36px; width: auto; object-fit: contain; }
    @media(min-width:768px){ .fp-logo img{ height: 44px; } }

    .fp-logo-text h1 {
      font-size: 1rem;
      font-weight: 800;
      background: linear-gradient(135deg, #059669, #0891b2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.1;
      white-space: nowrap;
    }
    @media(min-width:480px){ .fp-logo-text h1{ font-size: 1.15rem; } }
    @media(min-width:768px){ .fp-logo-text h1{ font-size: 1.4rem; } }

    .fp-logo-text span {
      display: none; font-size: 0.65rem; color: #6b7280; letter-spacing: 0.3px;
    }
    @media(min-width:480px){ .fp-logo-text span{ display: block; } }

    /* Desktop Nav */
    .fp-nav { display: none; }
    @media(min-width:768px){
      .fp-nav { display: flex; align-items: center; gap: 1.75rem; }
    }
    .fp-nav-link {
      color: #374151; font-weight: 600; font-size: 0.9rem;
      text-decoration: none; position: relative; transition: color 0.2s;
      white-space: nowrap;
    }
    .fp-nav-link:hover { color: #059669; }
    .fp-nav-link::after {
      content: ''; position: absolute; bottom: -3px; left: 0;
      width: 0; height: 2px; background: #059669; transition: width 0.3s;
    }
    .fp-nav-link:hover::after { width: 100%; }
    .fp-nav-link.active { color: #059669; }
    .fp-nav-link.active::after { width: 100%; }

    /* Header Actions */
    .fp-actions { display: flex; align-items: center; gap: 0.5rem; }

    .fp-icon-btn {
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%; border: none; background: #f3f4f6;
      color: #374151; cursor: pointer; transition: all 0.2s; flex-shrink: 0;
      position: relative;
    }
    .fp-icon-btn:hover { background: #e5e7eb; color: #059669; }
    @media(min-width:768px){ .fp-icon-btn{ width: 40px; height: 40px; } }

    .fp-cart-badge {
      position: absolute; top: -6px; right: -6px;
      background: linear-gradient(135deg,#ef4444,#dc2626);
      color: white; font-size: 0.65rem; font-weight: 700;
      width: 1.1rem; height: 1.1rem;
      display: flex; align-items: center; justify-content: center;
      border-radius: 9999px; border: 2px solid white;
    }

    .fp-shop-btn {
      display: flex; align-items: center; gap: 0.4rem;
      padding: 0.45rem 1rem;
      background: linear-gradient(135deg,#10b981,#059669);
      color: white; font-weight: 700; font-size: 0.82rem;
      border-radius: 9999px; border: none; cursor: pointer;
      transition: all 0.3s; white-space: nowrap; flex-shrink: 0;
    }
    .fp-shop-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 12px rgba(16,185,129,0.3);
    }
    @media(min-width:768px){
      .fp-shop-btn{ padding: 0.55rem 1.4rem; font-size: 0.9rem; }
    }

    /* Mobile Drawer */
    .fp-mobile-menu { position: fixed; inset: 0; z-index: 100; pointer-events: none; }
    .fp-mobile-menu.open { pointer-events: all; }
    .fp-mobile-overlay {
      position: absolute; inset: 0; background: rgba(0,0,0,0); transition: background 0.3s;
    }
    .fp-mobile-menu.open .fp-mobile-overlay { background: rgba(0,0,0,0.4); }
    .fp-mobile-drawer {
      position: absolute; top: 0; right: 0;
      width: min(280px,85vw); height: 100%;
      background: white; transform: translateX(100%);
      transition: transform 0.35s cubic-bezier(0.4,0,0.2,1);
      display: flex; flex-direction: column;
      box-shadow: -8px 0 30px rgba(0,0,0,0.1);
    }
    .fp-mobile-menu.open .fp-mobile-drawer { transform: translateX(0); }
    .fp-mobile-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 1rem 1.25rem; border-bottom: 1px solid #f3f4f6;
    }
    .fp-mobile-nav { flex: 1; padding: 1rem 0; overflow-y: auto; }
    .fp-mobile-link {
      display: flex; align-items: center; padding: 0.85rem 1.5rem;
      font-size: 1rem; font-weight: 600; color: #374151;
      text-decoration: none; border-left: 3px solid transparent; transition: all 0.2s;
    }
    .fp-mobile-link:hover, .fp-mobile-link.active {
      background: #f0fdf4; color: #059669; border-left-color: #059669;
    }

    /* ── PAGE HEADER ── */
    .fp-page-header {
      background: linear-gradient(135deg,#f0fdf4,#ffffff,#f0f9ff);
      padding: 2rem 0;
      border-bottom: 1px solid rgba(229,231,235,0.5);
    }

    .fp-page-title {
      font-size: 1.75rem; font-weight: 900;
      background: linear-gradient(135deg,#059669,#0891b2);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      margin-bottom: 0.4rem;
    }
    @media(min-width:768px){ .fp-page-title{ font-size: 2.25rem; } }

    .fp-page-sub { color: #6b7280; font-size: 1rem; }

    .fp-mock-banner {
      display: inline-flex; align-items: center; gap: 0.5rem;
      background: #fef3c7; border: 1px solid #fde68a;
      color: #92400e; font-size: 0.8rem; font-weight: 600;
      padding: 0.4rem 1rem; border-radius: 9999px; margin-top: 0.75rem;
    }

    /* ── SEARCH CARD ── */
    .fp-search-card {
      background: white;
      border-radius: 1.5rem;
      padding: 1.5rem;
      margin-bottom: 2rem;
      border: 1px solid #f3f4f6;
      box-shadow: 0 4px 16px rgba(0,0,0,0.03);
    }

    .fp-search-row {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    @media(min-width:640px){ .fp-search-row{ grid-template-columns: 1fr auto; gap: 1rem; } }

    .fp-search-wrap {
      position: relative; display: flex; align-items: center;
    }
    .fp-search-icon {
      position: absolute; left: 1rem;
      width: 1.1rem; height: 1.1rem; color: #9ca3af; pointer-events: none;
    }
    .fp-search-input {
      width: 100%;
      padding: 0.7rem 1rem 0.7rem 2.6rem;
      border: 2px solid #e5e7eb; border-radius: 9999px;
      font-size: 0.9rem; background: #f9fafb;
      transition: all 0.2s;
      font-family: inherit;
    }
    .fp-search-input:focus {
      outline: none; border-color: #10b981; background: white;
      box-shadow: 0 0 0 3px rgba(16,185,129,0.1);
    }

    .fp-select {
      padding: 0.7rem 2.5rem 0.7rem 1rem;
      border: 2px solid #e5e7eb; border-radius: 9999px;
      font-size: 0.9rem; background: #f9fafb; cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
      background-position: right 0.75rem center;
      background-repeat: no-repeat; background-size: 1.25em;
      transition: all 0.2s; font-family: inherit; white-space: nowrap;
    }
    .fp-select:focus { outline: none; border-color: #10b981; background-color: white; }

    .fp-results-count {
      margin-top: 0.75rem; font-size: 0.82rem; color: #6b7280;
    }

    /* Category pills */
    .fp-pills {
      display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1rem;
    }
    .fp-pill {
      padding: 0.3rem 0.9rem;
      border-radius: 9999px; font-size: 0.8rem; font-weight: 600;
      border: 1.5px solid #e5e7eb; background: white; color: #374151;
      cursor: pointer; transition: all 0.2s; font-family: inherit;
    }
    .fp-pill:hover { border-color: #10b981; color: #059669; }
    .fp-pill.active {
      background: #059669; color: white; border-color: #059669;
    }

    /* ── PRODUCT GRID ── */
    .fp-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }
    @media(min-width:640px){ .fp-grid{ grid-template-columns: repeat(2,1fr); gap: 1rem; } }
    @media(min-width:768px){ .fp-grid{ grid-template-columns: repeat(3,1fr); gap: 1.25rem; } }
    @media(min-width:1280px){ .fp-grid{ grid-template-columns: repeat(4,1fr); gap: 1.5rem; } }

    /* ── PRODUCT CARD ── */
    .fp-card {
      background: white;
      border: 1px solid #f3f4f6;
      border-radius: 1.25rem;
      overflow: hidden;
      display: flex; flex-direction: column;
      transition: all 0.35s cubic-bezier(0.165,0.84,0.44,1);
      position: relative;
    }
    .fp-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 16px 30px rgba(5,150,105,0.1);
      border-color: rgba(5,150,105,0.2);
    }

    /* Product image / emoji area */
    .fp-card-img {
      position: relative;
      height: 160px;
      background: linear-gradient(145deg,#f0fdf4,#f0f9ff);
      display: flex; align-items: center; justify-content: center;
      overflow: hidden;
    }
    @media(min-width:640px){ .fp-card-img{ height: 185px; } }

    .fp-card-img img {
      width: 100%; height: 100%;
      object-fit: cover;
    }

    .fp-card-emoji {
      font-size: 3.5rem;
      line-height: 1;
    }
    @media(min-width:640px){ .fp-card-emoji{ font-size: 4rem; } }

    .fp-card-category {
      position: absolute; bottom: 0.6rem; left: 0.6rem;
      background: rgba(255,255,255,0.92); backdrop-filter: blur(4px);
      padding: 0.2rem 0.65rem; border-radius: 9999px;
      font-size: 0.65rem; font-weight: 700; color: #059669;
      border: 1px solid rgba(5,150,105,0.15); text-transform: capitalize;
    }

    .fp-card-badge {
      position: absolute; top: 0.6rem; left: 0.6rem;
      background: #f59e0b; color: white;
      font-size: 0.6rem; font-weight: 700;
      padding: 0.2rem 0.55rem; border-radius: 9999px;
    }

    .fp-wishlist-btn {
      position: absolute; top: 0.6rem; right: 0.6rem;
      width: 2rem; height: 2rem;
      background: rgba(255,255,255,0.9); backdrop-filter: blur(4px);
      border-radius: 50%; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.2s; color: #9ca3af;
    }
    .fp-wishlist-btn:hover { color: #ef4444; transform: scale(1.1); }
    .fp-wishlist-btn.active { color: #ef4444; }
    .fp-wishlist-btn svg { width: 1rem; height: 1rem; }

    /* Card body */
    .fp-card-body {
      padding: 0.85rem 0.9rem 1rem;
      flex: 1; display: flex; flex-direction: column;
    }
    @media(min-width:640px){ .fp-card-body{ padding: 1rem 1.1rem 1.1rem; } }

    .fp-card-name {
      font-size: 0.88rem; font-weight: 800; color: #111827;
      margin-bottom: 0.25rem; line-height: 1.3;
    }
    @media(min-width:640px){ .fp-card-name{ font-size: 0.95rem; } }

    .fp-card-desc {
      font-size: 0.75rem; color: #9ca3af; line-height: 1.45;
      margin-bottom: 0.6rem; flex: 1;
      display: -webkit-box; -webkit-line-clamp: 2;
      -webkit-box-orient: vertical; overflow: hidden;
    }

    .fp-card-stock {
      display: flex; align-items: center; gap: 0.35rem;
      font-size: 0.72rem; color: #6b7280; margin-bottom: 0.65rem;
    }
    .fp-stock-dot {
      width: 0.45rem; height: 0.45rem; border-radius: 50%;
    }
    .fp-stock-in  { background: #10b981; }
    .fp-stock-low { background: #f59e0b; }
    .fp-stock-out { background: #ef4444; }

    .fp-card-footer {
      display: flex; align-items: center; justify-content: space-between;
      gap: 0.5rem; margin-top: auto;
    }

    .fp-price {
      font-size: 1.1rem; font-weight: 900; color: #059669;
    }
    @media(min-width:640px){ .fp-price{ font-size: 1.25rem; } }

    .fp-add-btn {
      display: flex; align-items: center; gap: 0.3rem;
      padding: 0.45rem 0.9rem;
      background: linear-gradient(135deg,#059669,#10b981);
      color: white; border: none; border-radius: 9999px;
      font-weight: 700; font-size: 0.78rem; cursor: pointer;
      transition: all 0.25s; font-family: inherit; white-space: nowrap;
    }
    .fp-add-btn:hover { transform: scale(1.05); box-shadow: 0 6px 12px rgba(5,150,105,0.3); }
    .fp-add-btn svg { width: 0.9rem; height: 0.9rem; }

    /* ── EMPTY STATE ── */
    .fp-empty {
      text-align: center; padding: 4rem 2rem;
      background: white; border-radius: 2rem;
      border: 2px dashed #e5e7eb;
    }
    .fp-empty svg { width: 4rem; height: 4rem; color: #d1d5db; margin: 0 auto 1rem; }

    /* ── TRUST BAR ── */
    .fp-trust-bar {
      background: white;
      border-top: 1px solid #e5e7eb;
      border-bottom: 1px solid #e5e7eb;
      padding: 1rem 0; margin-top: 3rem;
    }
    .fp-trust-items {
      display: flex; flex-wrap: wrap; align-items: center;
      justify-content: center; gap: 1rem 2rem;
    }
    .fp-trust-item { display: flex; align-items: center; gap: 0.5rem; }
    .fp-trust-icon {
      width: 1.75rem; height: 1.75rem; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
    }
    .fp-trust-item span { font-size: 0.82rem; font-weight: 600; color: #374151; }

    /* ── SPINNER ── */
    .fp-spinner {
      width: 3rem; height: 3rem;
      border: 3px solid #e5e7eb;
      border-top-color: #059669;
      border-radius: 50%;
      animation: spin 0.9s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* ── FOOTER ── */
    .fp-footer {
      background: #0f172a; color: white;
      padding: 4rem 0 2rem; margin-top: 0;
    }
    .fp-footer-grid {
      display: grid; grid-template-columns: 1fr;
      gap: 2.5rem; margin-bottom: 3rem;
    }
    @media(min-width:640px){ .fp-footer-grid{ grid-template-columns: repeat(2,1fr); } }
    @media(min-width:1024px){ .fp-footer-grid{ grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 3rem; } }

    .fp-footer-logo {
      display: flex; align-items: center; gap: 0.75rem;
      margin-bottom: 1.25rem; cursor: pointer;
    }
    .fp-footer-logo img { height: 44px; }
    .fp-footer-logo h3 {
      font-size: 1.35rem; font-weight: 800;
      background: linear-gradient(135deg,#34d399,#22d3ee);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    .fp-footer-about { color: #94a3b8; line-height: 1.7; font-size: 0.9rem; margin-bottom: 1.5rem; }
    .fp-social-row { display: flex; gap: 0.75rem; }
    .fp-social-btn {
      width: 2.25rem; height: 2.25rem; background: #1e293b;
      border-radius: 0.65rem; display: flex; align-items: center; justify-content: center;
      color: white; transition: all 0.3s; text-decoration: none;
    }
    .fp-social-btn:hover { background: #2563eb; transform: translateY(-2px); }
    .fp-social-btn svg { width: 1rem; height: 1rem; }

    .fp-footer-col-title {
      font-size: 1rem; font-weight: 700; color: white;
      margin-bottom: 1.25rem; display: flex; align-items: center; gap: 0.4rem;
    }
    .fp-footer-links { list-style: none; }
    .fp-footer-links li { margin-bottom: 0.75rem; }
    .fp-footer-links a {
      color: #94a3b8; text-decoration: none; font-size: 0.9rem;
      transition: all 0.2s; display: inline-flex; align-items: center; gap: 0.35rem;
    }
    .fp-footer-links a:hover { color: white; transform: translateX(4px); }

    .fp-footer-contact { list-style: none; }
    .fp-footer-contact li {
      display: flex; align-items: flex-start; gap: 0.65rem;
      color: #94a3b8; font-size: 0.9rem; margin-bottom: 0.85rem;
    }
    .fp-footer-contact li svg { width: 1.1rem; height: 1.1rem; flex-shrink: 0; margin-top: 0.1rem; }

    .fp-newsletter { margin-top: 1.5rem; }
    .fp-newsletter-label { font-size: 0.9rem; font-weight: 600; color: white; margin-bottom: 0.75rem; }
    .fp-newsletter-row { display: flex; gap: 0.4rem; }
    .fp-newsletter-row input {
      flex: 1; padding: 0.65rem 1rem; border-radius: 9999px;
      border: 1px solid #334155; background: #1e293b; color: white;
      font-size: 0.85rem; min-width: 0; font-family: inherit;
    }
    .fp-newsletter-row input::placeholder { color: #64748b; }
    .fp-newsletter-btn {
      background: #2563eb; color: white; border: none; border-radius: 9999px;
      padding: 0.65rem 1.1rem; font-weight: 700; font-size: 0.82rem;
      cursor: pointer; transition: all 0.3s; font-family: inherit; white-space: nowrap;
    }
    .fp-newsletter-btn:hover { background: #1d4ed8; transform: translateY(-1px); }

    .fp-footer-bottom {
      border-top: 1px solid #1e293b; padding-top: 2rem;
      display: flex; flex-direction: column;
      align-items: center; gap: 1rem; text-align: center;
    }
    @media(min-width:768px){
      .fp-footer-bottom { flex-direction: row; justify-content: space-between; text-align: left; }
    }
    .fp-copyright { color: #64748b; font-size: 0.82rem; }
    .fp-footer-bottom-links { display: flex; flex-wrap: wrap; gap: 1.5rem; justify-content: center; }
    .fp-footer-bottom-links a {
      color: #94a3b8; text-decoration: none; font-size: 0.82rem; transition: color 0.2s;
    }
    .fp-footer-bottom-links a:hover { color: white; }
    .fp-seo-link {
      color: #f59e0b !important; font-weight: 700;
      display: inline-flex; align-items: center; gap: 0.35rem;
    }
    .fp-seo-link:hover { color: #fbbf24 !important; }

    @keyframes heartbeat {
      0%,100%{ transform: scale(1); } 25%,75%{ transform: scale(1.15); }
    }
    .animate-heart { animation: heartbeat 1.4s ease-in-out infinite; }

    /* Utils */
    .flex   { display: flex; }
    .items-center  { align-items: center; }
    .justify-between { justify-content: space-between; }
  `;

  // ─── LOADING ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#f9fafb' }}>
          <div className="fp-spinner" />
        </div>
      </>
    );
  }

  // ─── RENDER ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>

        {/* ── HEADER ── */}
        <header className={`fp-header${scrolled ? ' scrolled' : ''}`}>
          <div className="fp-container">
            <div className="fp-header-inner">
              {/* Logo */}
              <div className="fp-logo" onClick={() => navigate('/')}>
                <img
                  src="/images/logo.png"
                  alt="Fresh Hills"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }}
                />
                <div className="fp-logo-text">
                  <h1>Fresh Hills</h1>
                  <span>Fresh & Healthy Since 2024</span>
                </div>
              </div>

              {/* Desktop Nav */}
              <nav className="fp-nav">
                <a href="/"              className="fp-nav-link">Home</a>
                <a href="/products"      className="fp-nav-link active">Products</a>
                <a href="/#offers"       className="fp-nav-link">Offers</a>
                <a href="/#how-it-works" className="fp-nav-link">How It Works</a>
                <a href="/#contact"      className="fp-nav-link">Contact</a>
              </nav>

              {/* Actions */}
              <div className="fp-actions">
                {/* Cart */}
                <button
                  className="fp-icon-btn"
                  title="View Cart"
                  onClick={() => navigate('/cart')}
                >
                  <ShoppingCartIcon style={{ width: '1.1rem', height: '1.1rem' }} />
                  {cartCount > 0 && (
                    <span className="fp-cart-badge">{cartCount > 9 ? '9+' : cartCount}</span>
                  )}
                </button>

                {/* Shop Now */}
                <button className="fp-shop-btn" onClick={() => navigate('/products')}>
                  <ShoppingBagIcon style={{ width: '0.95rem', height: '0.95rem' }} />
                  <span>Shop Now</span>
                </button>

                {/* Hamburger */}
                <button
                  className="fp-icon-btn"
                  style={{ display: 'flex' }}
                  onClick={() => setMobileMenuOpen(true)}
                  title="Menu"
                >
                  <Bars3Icon style={{ width: '1.2rem', height: '1.2rem' }} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* ── MOBILE DRAWER ── */}
        <div className={`fp-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
          <div className="fp-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
          <div className="fp-mobile-drawer">
            <div className="fp-mobile-header">
              <div className="fp-logo">
                <img src="/images/logo.png" alt="Fresh Hills"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/36?text=FH'; }}
                  style={{ height: '32px' }}
                />
                <div className="fp-logo-text"><h1>Fresh Hills</h1></div>
              </div>
              <button className="fp-icon-btn" onClick={() => setMobileMenuOpen(false)}>
                <XMarkIcon style={{ width: '1.2rem', height: '1.2rem' }} />
              </button>
            </div>
            <nav className="fp-mobile-nav">
              {[
                { label: 'Home',         href: '/' },
                { label: 'Products',     href: '/products' },
                { label: 'Offers',       href: '/#offers' },
                { label: 'How It Works', href: '/#how-it-works' },
                { label: 'Contact',      href: '/#contact' },
              ].map((item) => (
                <a key={item.label} href={item.href}
                  className={`fp-mobile-link${item.href === '/products' ? ' active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #f3f4f6' }}>
              <button
                className="fp-shop-btn"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setMobileMenuOpen(false); navigate('/cart'); }}
              >
                <ShoppingCartIcon style={{ width: '1rem', height: '1rem' }} />
                View Cart {cartCount > 0 ? `(${cartCount})` : ''}
              </button>
            </div>
          </div>
        </div>

        {/* ── PAGE HEADER ── */}
        <div className="fp-page-header">
          <div className="fp-container">
            <h1 className="fp-page-title">Our Fresh Products</h1>
            <p className="fp-page-sub">Farm-fresh dairy, ready for pickup or delivery.</p>
            {usingMockData && (
              <div className="fp-mock-banner">
                ⚠️ Showing sample products — backend not connected
              </div>
            )}
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="fp-container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>

          {/* Search & Filter */}
          <div className="fp-search-card">
            <div className="fp-search-row">
              <div className="fp-search-wrap">
                <MagnifyingGlassIcon className="fp-search-icon" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="fp-search-input"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="fp-select"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Pills */}
            <div className="fp-pills">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`fp-pill${selectedCategory === cat ? ' active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>

            <div className="fp-results-count">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
            </div>
          </div>

          {/* Products Grid */}
          {filteredProducts.length === 0 ? (
            <div className="fp-empty">
              <MagnifyingGlassIcon />
              <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#374151', marginBottom: '0.5rem' }}>
                No products found
              </h3>
              <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
                Try adjusting your search or filter.
              </p>
              <button
                onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
                style={{
                  background: 'linear-gradient(135deg,#10b981,#059669)',
                  color: 'white', border: 'none', borderRadius: '9999px',
                  padding: '0.65rem 1.5rem', fontWeight: '700', fontSize: '0.9rem',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="fp-grid">
              {filteredProducts.map((product) => {
                const imgSrc   = getImageSrc(product);
                const isWished = wishlist.includes(product.id);
                const isLow    = product.stock > 0 && product.stock <= 10;
                const isOut    = product.stock === 0;

                return (
                  <div key={product.id} className="fp-card">
                    {/* Image / Emoji */}
                    <div className="fp-card-img">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={product.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {/* Emoji fallback (always rendered, hidden when img loads) */}
                      <span
                        className="fp-card-emoji"
                        style={{ display: imgSrc ? 'none' : 'block' }}
                      >
                        {product.emoji || '🥛'}
                      </span>

                      {product.badge && (
                        <span className="fp-card-badge">{product.badge}</span>
                      )}
                      <span className="fp-card-category">
                        {product.category || 'Dairy'}
                      </span>
                      <button
                        className={`fp-wishlist-btn${isWished ? ' active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
                        title="Add to wishlist"
                      >
                        <HeartIcon style={{ fill: isWished ? '#ef4444' : 'none' }} />
                      </button>
                    </div>

                    {/* Body */}
                    <div className="fp-card-body">
                      <div className="fp-card-name">{product.name}</div>
                      <div className="fp-card-desc">
                        {product.description || 'Fresh and pure, sourced directly from local farms.'}
                      </div>

                      {/* Stock indicator */}
                      {product.stock !== undefined && (
                        <div className="fp-card-stock">
                          <span className={`fp-stock-dot ${isOut ? 'fp-stock-out' : isLow ? 'fp-stock-low' : 'fp-stock-in'}`} />
                          <span>
                            {isOut ? 'Out of stock' : isLow ? `Only ${product.stock} left` : 'In stock'}
                          </span>
                        </div>
                      )}

                      <div className="fp-card-footer">
                        <span className="fp-price">₹{product.price}</span>
                        <button
                          className="fp-add-btn"
                          onClick={() => handleAddToCart(product)}
                          disabled={isOut}
                          style={{ opacity: isOut ? 0.5 : 1, cursor: isOut ? 'not-allowed' : 'pointer' }}
                        >
                          <ShoppingCartIcon />
                          {isOut ? 'Sold Out' : 'Add'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── TRUST BAR ── */}
        <div className="fp-trust-bar">
          <div className="fp-container">
            <div className="fp-trust-items">
              {[
                { icon: <BoltIcon style={{ width:'1rem', height:'1rem', color:'#059669' }} />, bg: '#d1fae5', label: 'Same-Day Delivery' },
                { icon: <CurrencyRupeeIcon style={{ width:'1rem', height:'1rem', color:'#2563eb' }} />, bg: '#dbeafe', label: 'Cashless Payment' },
                { icon: <CubeIcon style={{ width:'1rem', height:'1rem', color:'#d97706' }} />, bg: '#fef3c7', label: 'Fresh Stock Daily' },
                { icon: <UserGroupIcon style={{ width:'1rem', height:'1rem', color:'#9333ea' }} />, bg: '#fae8ff', label: '10k+ Customers' },
              ].map((t) => (
                <div key={t.label} className="fp-trust-item">
                  <div className="fp-trust-icon" style={{ background: t.bg }}>{t.icon}</div>
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer id="contact" className="fp-footer">
          <div className="fp-container">
            <div className="fp-footer-grid">
              {/* Brand */}
              <div>
                <div className="fp-footer-logo" onClick={() => navigate('/')}>
                  <img src="/images/logo.png" alt="Fresh Hills"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }}
                  />
                  <h3>Fresh Hills</h3>
                </div>
                <p className="fp-footer-about">
                  India's trusted online dairy store. Fresh milk, eggs, butter and more — 
                  delivered to your door every morning since 2024.
                </p>
                <div className="fp-social-row">
                  {['fb', 'tw', 'ig'].map((s) => (
                    <a key={s} href="#" className="fp-social-btn">
                      <svg fill="currentColor" viewBox="0 0 24 24" style={{ width:'1rem', height:'1rem' }}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="fp-footer-col-title">
                  <HeartIcon style={{ width:'1rem', height:'1rem', color:'#ef4444' }} />
                  Quick Links
                </h4>
                <ul className="fp-footer-links">
                  {[
                    { label: 'Home',         href: '/' },
                    { label: 'Products',     href: '/products' },
                    { label: 'Offers',       href: '/#offers' },
                    { label: 'How It Works', href: '/#how-it-works' },
                    { label: 'Contact',      href: '/#contact' },
                  ].map((l) => (
                    <li key={l.label}>
                      <a href={l.href}>
                        <ChevronRightIcon style={{ width:'0.7rem', height:'0.7rem' }} />{l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Products */}
              <div>
                <h4 className="fp-footer-col-title">
                  <HeartIcon style={{ width:'1rem', height:'1rem', color:'#ef4444' }} />
                  Our Products
                </h4>
                <ul className="fp-footer-links">
                  {['Fresh Milk','Butter & Cream','Cheese','Farm Eggs','Curd & Yogurt'].map((p) => (
                    <li key={p}>
                      <a href="/products">
                        <ChevronRightIcon style={{ width:'0.7rem', height:'0.7rem' }} />{p}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="fp-footer-col-title">
                  <HeartIcon style={{ width:'1rem', height:'1rem', color:'#ef4444' }} />
                  Contact Us
                </h4>
                <ul className="fp-footer-contact">
                  <li>
                    <MapPinIcon />
                    <span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Thrikkakara, Ernakulam, Kerala, 682021</span>
                  </li>
                  <li>
                    <DevicePhoneMobileIcon />
                    <span>+91 70108 44160</span>
                  </li>
                  <li>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width:'1.1rem', height:'1.1rem', flexShrink:0 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>hello@fesiconenterprises.com</span>
                  </li>
                </ul>
                <div className="fp-newsletter">
                  <div className="fp-newsletter-label">Subscribe for offers</div>
                  <div className="fp-newsletter-row">
                    <input type="email" placeholder="Your email address" />
                    <button className="fp-newsletter-btn">Go</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom */}
            <div className="fp-footer-bottom">
              <div className="fp-copyright">
                © {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in India
                <br />
                <span style={{ fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>
                  GST: 32AAGFF6427Q1Z2
                </span>
              </div>
              <div className="fp-footer-bottom-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="#">Cookie Policy</a>
                <a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="fp-seo-link">
                  SEO9INE
                  <HeartIcon className="animate-heart" style={{ width:'0.85rem', height:'0.85rem', color:'#ef4444' }} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Products;